import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Mail, RefreshCw, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CheckEmail = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const { toast } = useToast();
  
  const email = location.state?.email || '';
  const [resendEmail, setResendEmail] = useState(email);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/resend-verification`, { email: resendEmail });
      setSent(true);
      toast({
        title: language === 'es' ? '¡Correo enviado!' : 'Email Sent!',
        description: language === 'es' 
          ? 'Revisa tu bandeja de entrada.' 
          : 'Check your inbox.',
      });
    } catch (error) {
      toast({
        title: language === 'es' ? 'Error' : 'Error',
        description: error.response?.data?.detail || 
          (language === 'es' ? 'No se pudo enviar el correo' : 'Failed to send email'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center py-32 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_75f4a617-7223-46ab-b5af-9fb0ce34d58c/artifacts/s7ixbz5d_customcolor_logo_transparent_background.png" 
            alt="Exciting.Travel" 
            className="h-20 w-auto mx-auto mb-6"
          />
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="bg-[#84bc00]/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-[#84bc00]" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {language === 'es' ? 'Revisa tu correo' : 'Check Your Email'}
          </h1>
          
          <p className="text-gray-600 mb-6">
            {language === 'es' 
              ? `Hemos enviado un enlace de verificación a ${email || 'tu correo'}. Haz clic en el enlace para activar tu cuenta.`
              : `We've sent a verification link to ${email || 'your email'}. Click the link to activate your account.`}
          </p>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">
              {language === 'es' ? '¿No recibiste el correo?' : "Didn't receive the email?"}
            </h3>
            <ul className="text-sm text-gray-600 text-left space-y-1">
              <li>• {language === 'es' ? 'Revisa tu carpeta de spam' : 'Check your spam folder'}</li>
              <li>• {language === 'es' ? 'Verifica que tu correo sea correcto' : 'Verify your email is correct'}</li>
              <li>• {language === 'es' ? 'El correo puede tardar unos minutos' : 'The email may take a few minutes'}</li>
            </ul>
          </div>

          <form onSubmit={handleResend} className="space-y-4">
            <Input
              type="email"
              placeholder={language === 'es' ? 'Tu correo electrónico' : 'Your email address'}
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              className="border-2"
              required
            />
            
            <Button
              type="submit"
              disabled={loading || sent}
              className="w-full bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : sent ? (
                <CheckCircle className="w-4 h-4 mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {sent 
                ? (language === 'es' ? '¡Enviado!' : 'Sent!') 
                : (language === 'es' ? 'Reenviar correo de verificación' : 'Resend Verification Email')}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t">
            <Link 
              to="/login" 
              className="text-[#84bc00] hover:text-[#6a9600] font-semibold"
            >
              {language === 'es' ? 'Volver al inicio de sesión' : 'Back to Login'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckEmail;
